<template>
  <div v-if="visible" class="ai-attach-bar">
    <v-btn
      :disabled="disabled || atLimit"
      icon
      small
      class="ai-attach-btn"
      :title="atLimit ? ('Máximo de ' + maxFiles + ' arquivos') : 'Anexar arquivo'"
      @click="openPicker"
    >
      <v-icon size="20" color="primary">{{ mdiPaperclip }}</v-icon>
    </v-btn>

    <input
      ref="fileInput"
      type="file"
      :accept="accept"
      multiple
      style="display: none"
      @change="onFilesSelected"
    />

    <div v-if="value && value.length" class="ai-attach-chips">
      <div
        v-for="(f, idx) in value"
        :key="idx"
        class="ai-attach-chip"
        :title="f.name"
      >
        <v-icon size="14" color="primary" class="mr-1">{{ iconFor(f.mimeType) }}</v-icon>
        <span class="caption text-truncate" style="max-width: 140px">{{ f.name }}</span>
        <span class="caption grey--text ml-1">{{ formatSize(f.size) }}</span>
        <v-btn
          icon
          x-small
          class="ml-1"
          @click.stop="removeAt(idx)"
          :disabled="disabled"
        >
          <v-icon size="14">{{ mdiClose }}</v-icon>
        </v-btn>
      </div>
    </div>

    <v-snackbar v-model="showError" :timeout="3500" color="error" bottom>
      {{ errorText }}
    </v-snackbar>
  </div>
</template>

<script>
import { mdiPaperclip, mdiClose, mdiFileDocumentOutline, mdiFilePdfBox, mdiImageOutline, mdiFileTableOutline } from "@mdi/js";

const ACCEPT = "application/pdf,image/png,image/jpeg,image/webp,text/plain,text/csv,text/markdown,.md";
const ALLOWED_MIMES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/plain',
  'text/csv',
  'text/markdown',
]);
const EXT_TO_MIME = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  txt: 'text/plain',
  csv: 'text/csv',
  md: 'text/markdown',
};
const MAX_FILES = 3;
const MAX_BIN = 6 * 1024 * 1024;
const MAX_TXT = 1 * 1024 * 1024;
const MAX_TOTAL = 6 * 1024 * 1024;

const isTextLike = (m) => m === 'text/plain' || m === 'text/csv' || m === 'text/markdown';

export default {
  name: "AiAttachmentPicker",
  props: {
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    visible: { type: Boolean, default: true },
  },
  data() {
    return {
      accept: ACCEPT,
      maxFiles: MAX_FILES,
      mdiPaperclip,
      mdiClose,
      showError: false,
      errorText: "",
    };
  },
  computed: {
    atLimit() {
      return (this.value || []).length >= MAX_FILES;
    },
  },
  methods: {
    iconFor(mime) {
      if (!mime) return mdiFileDocumentOutline;
      if (mime.startsWith('image/')) return mdiImageOutline;
      if (mime === 'application/pdf') return mdiFilePdfBox;
      if (mime === 'text/csv') return mdiFileTableOutline;
      return mdiFileDocumentOutline;
    },
    formatSize(bytes) {
      if (!bytes && bytes !== 0) return '';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
      return (bytes / 1024 / 1024).toFixed(1) + ' MB';
    },
    showErr(text) {
      this.errorText = text;
      this.showError = true;
    },
    openPicker() {
      if (this.atLimit) {
        this.showErr('Máximo de ' + MAX_FILES + ' arquivos por mensagem.');
        return;
      }
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    resolveMime(file) {
      if (file.type && ALLOWED_MIMES.has(file.type)) return file.type;
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      return EXT_TO_MIME[ext] || file.type || '';
    },
    async readFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          const b64 = typeof dataUrl === 'string' && dataUrl.indexOf('base64,') >= 0
            ? dataUrl.split('base64,')[1]
            : '';
          resolve(b64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    async onFilesSelected(e) {
      const picked = Array.from(e.target.files || []);
      if (!picked.length) return;

      const current = Array.isArray(this.value) ? [...this.value] : [];
      const currentTotal = current.reduce((acc, f) => acc + (f.size || 0), 0);

      let added = 0;
      let totalAfter = currentTotal;

      for (const file of picked) {
        if (current.length + added >= MAX_FILES) {
          this.showErr('Máximo de ' + MAX_FILES + ' arquivos por mensagem.');
          break;
        }
        const mimeType = this.resolveMime(file);
        if (!ALLOWED_MIMES.has(mimeType)) {
          this.showErr('Tipo de arquivo não suportado: ' + (file.name || mimeType));
          continue;
        }
        const limit = isTextLike(mimeType) ? MAX_TXT : MAX_BIN;
        if (file.size > limit) {
          const mb = Math.round(limit / 1024 / 1024);
          this.showErr('"' + file.name + '" excede o limite de ' + mb + ' MB.');
          continue;
        }
        if (totalAfter + file.size > MAX_TOTAL) {
          this.showErr('Soma dos anexos excede 20 MB.');
          break;
        }

        let base64;
        try {
          base64 = await this.readFile(file);
        } catch {
          this.showErr('Falha ao ler "' + file.name + '".');
          continue;
        }
        if (!base64) {
          this.showErr('Falha ao ler "' + file.name + '".');
          continue;
        }

        current.push({
          name: file.name,
          mimeType,
          size: file.size,
          base64,
        });
        added += 1;
        totalAfter += file.size;
      }

      this.$emit('input', current);
    },
    removeAt(index) {
      const next = Array.isArray(this.value) ? [...this.value] : [];
      next.splice(index, 1);
      this.$emit('input', next);
    },
  },
};
</script>

<style>
.ai-attach-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 8px;
}
.ai-attach-btn {
  flex-shrink: 0;
}
.ai-attach-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.ai-attach-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(25, 49, 83, 0.06);
  border-radius: 14px;
  padding: 2px 6px 2px 10px;
  font-size: 12px;
  max-width: 100%;
}
</style>
